// views/Profile/components/UsageSummaryPanel.jsx
import React, { useState } from 'react';
import { TrendingUp, AlertCircle, ChevronDown } from 'lucide-react';
import DateRangePicker from './DateRangePicker';
import EmptyState from '../../../components/ui/EmptyState';
import './UsageSummaryPanel.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Resource label catalog. Sentence-case (only the first word is
// capitalized) so the column reads consistently — no mixing of
// "Workflow Step" with "Workflow runs". When a new resourceType
// shows up that isn't in this map, the fallback (`humanizeResource`)
// also lowercases follow-on words.
const RESOURCE_LABELS = {
  WORKFLOW_EXECUTION: 'Workflow runs',
  WORKFLOW_STEP: 'Workflow steps',
  ASSISTANT_REQUEST: 'Agent requests',
  ASSISTANT_CALL: 'Agent requests',
  DATABASE_CALL: 'Database calls',
  DATABASE_READ: 'Database reads',
  DATABASE_WRITE: 'Database writes',
  DATABASE_QUERY: 'Database queries',
  DATA_TRANSFER_GB: 'Data transfer',
  TABLE_OPERATION: 'Table operations',
  INTEGRATION_CALL: 'Integration calls',
  BONUS: 'Bonus credits',
};

const humanizeResource = (raw) => {
  if (!raw) return 'Other';
  if (RESOURCE_LABELS[raw]) return RESOURCE_LABELS[raw];
  // Generic fallback: take the first word capitalized, the rest
  // lowercase — e.g. UNKNOWN_TYPE -> "Unknown type".
  const words = raw.split('_');
  return words
    .map((word, i) =>
      i === 0
        ? word.charAt(0) + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');
};

// Five violet shades that read clearly against the black radial card.
// Stable per index so the same resourceType always gets the same dot.
const SEGMENT_PALETTE = [
  '#6B3FC1',
  '#8E5BD9',
  '#B07EEC',
  '#D4A5FF',
  '#5C2EA8',
  '#7C4FCE',
];
const segmentColor = (index) =>
  SEGMENT_PALETTE[index % SEGMENT_PALETTE.length];

/**
 * UsageSummaryPanel — `GET /accounts/{id}/billing/usage-summary`.
 *
 * Renders the totalCharged hero, an allowance progress bar (when the
 * caller passes a `monthlyAllowance`), a stacked bar of resource
 * shares and a per-row list with count + coins + percent.
 *
 * Empty state: zero rows OR all rows totalCoins === 0.
 *
 * Props:
 *   data:             { periodStart, periodEnd, totalCharged, byResource[] }
 *   monthlyAllowance: number | null  (drives the allowance progress)
 *   range:            { preset, since, until } -> drives DateRangePicker
 *   onRangeChange:    (next) => void
 *   loading, error
 */
export default function UsageSummaryPanel({
  data,
  monthlyAllowance,
  range,
  onRangeChange,
  loading = false,
  error = null,
  title = 'Usage this period',
}) {
  const [showZeroRows, setShowZeroRows] = useState(false);

  const totalCharged = data?.totalCharged ?? 0;
  const rawRows = data?.byResource ?? [];
  const periodStart = data?.periodStart;
  const periodEnd = data?.periodEnd;

  const allowancePercent =
    monthlyAllowance && monthlyAllowance > 0
      ? Math.max(
          0,
          Math.min(100, (totalCharged / monthlyAllowance) * 100)
        )
      : null;

  // Sort by coins descending — the user reading "what consumed the most"
  // should see the heaviest rows first. Tiebreak by event count desc,
  // then alpha label so the order is deterministic across renders.
  const sortedRows = [...rawRows].sort((a, b) => {
    if (b.totalCoins !== a.totalCoins) return b.totalCoins - a.totalCoins;
    if ((b.count ?? 0) !== (a.count ?? 0)) return (b.count ?? 0) - (a.count ?? 0);
    return humanizeResource(a.resourceType).localeCompare(
      humanizeResource(b.resourceType)
    );
  });

  // Stable color map keyed on resourceType: the dot in the row list
  // and the segment in the stacked bar share the same color, in the
  // same sort order. Zero-coin rows still get a slot so they pick up
  // a consistent muted color when they're visible.
  const colorByResource = {};
  sortedRows.forEach((row, idx) => {
    colorByResource[row.resourceType] = segmentColor(idx);
  });

  const chargedRows = sortedRows.filter((row) => row.totalCoins > 0);
  const zeroRows = sortedRows.filter((row) => row.totalCoins === 0);

  const segments = chargedRows.map((row) => {
    const pct = totalCharged > 0 ? (row.totalCoins / totalCharged) * 100 : 0;
    return {
      ...row,
      color: colorByResource[row.resourceType],
      percent: pct,
    };
  });

  const visibleRows = showZeroRows ? sortedRows : chargedRows;

  const isEmpty = !loading && !error && rawRows.length === 0;
  const allZeros =
    !loading && !error && rawRows.length > 0 && totalCharged === 0;

  return (
    <section className="usage-panel">
      <header className="usage-panel__header">
        <div className="usage-panel__title">
          <TrendingUp size={16} aria-hidden="true" />
          <h2>{title}</h2>
        </div>
        <DateRangePicker value={range} onChange={onRangeChange} />
      </header>

      {loading && (
        <div className="usage-panel__skeleton" aria-busy="true">
          <div className="usage-panel__skeleton-hero" />
          <div className="usage-panel__skeleton-bar" />
          <div className="usage-panel__skeleton-row" />
          <div className="usage-panel__skeleton-row" />
          <div className="usage-panel__skeleton-row" />
        </div>
      )}

      {error && (
        <div className="usage-panel__error">
          <AlertCircle size={20} />
          <p>{error.message || 'Unable to load usage summary'}</p>
          {error.requestId && (
            <small className="usage-panel__request-id">
              ref: {error.requestId}
            </small>
          )}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="usage-panel__summary">
            {allowancePercent != null && (
              <div
                className="usage-panel__donut"
                style={{ '--pct': allowancePercent.toFixed(1) }}
                role="img"
                aria-label={`${Math.round(allowancePercent)}% of allowance used`}
              >
                <span className="usage-panel__donut-value">
                  {Math.round(allowancePercent)}%
                </span>
              </div>
            )}
            <div className="usage-panel__summary-text">
              <div className="usage-panel__hero">
                <span className="usage-panel__hero-number">
                  {formatCoins(totalCharged)}
                </span>
                {monthlyAllowance ? (
                  <span className="usage-panel__hero-suffix">
                    of {formatCoins(monthlyAllowance)} coin allowance
                  </span>
                ) : (
                  <span className="usage-panel__hero-suffix">coins used</span>
                )}
              </div>
              {allowancePercent != null && (
                <span className="usage-panel__progress-label">
                  {allowancePercent.toFixed(allowancePercent < 10 ? 1 : 0)}% of
                  allowance used
                </span>
              )}
            </div>
          </div>

          {!isEmpty && !allZeros && segments.length > 0 && (
            <div className="usage-panel__breakdown">
              <span className="usage-panel__breakdown-label">
                Breakdown by resource
              </span>
              <div
                className="usage-panel__stacked"
                role="img"
                aria-label="Usage breakdown by resource type"
              >
                {segments.map((seg) => (
                  <span
                    key={seg.resourceType}
                    className="usage-panel__stacked-seg"
                    style={{
                      width: `${seg.percent}%`,
                      background: seg.color,
                    }}
                    title={`${humanizeResource(seg.resourceType)}: ${formatCoins(
                      seg.totalCoins
                    )} (${seg.percent.toFixed(0)}%)`}
                  />
                ))}
              </div>
            </div>
          )}

          {(isEmpty || allZeros) && (
            <EmptyState
              variant="neutral"
              description={
                isEmpty
                  ? 'No usage in this range yet.'
                  : 'Nothing charged in this range — only audit-at-zero events.'
              }
            />
          )}

          {visibleRows.length > 0 && (
            <ul className="usage-panel__list">
              <li
                className="usage-panel__row usage-panel__row--header"
                aria-hidden="true"
              >
                <span />
                <span className="usage-panel__row-label">Resource</span>
                <span className="usage-panel__row-coins">Coins</span>
                <span className="usage-panel__row-count">Events</span>
                <span className="usage-panel__row-percent">Share</span>
              </li>
              {visibleRows.map((row) => {
                const isZero = row.totalCoins === 0;
                const dotColor = isZero
                  ? '#3a3a3a'
                  : colorByResource[row.resourceType];
                const pct =
                  totalCharged > 0
                    ? (row.totalCoins / totalCharged) * 100
                    : 0;
                return (
                  <li
                    key={row.resourceType}
                    className={`usage-panel__row${
                      isZero ? ' usage-panel__row--muted' : ''
                    }`}
                  >
                    <span
                      className="usage-panel__dot"
                      style={{ background: dotColor }}
                      aria-hidden="true"
                    />
                    <span className="usage-panel__row-label">
                      {humanizeResource(row.resourceType)}
                    </span>
                    <span className="usage-panel__row-coins">
                      {formatCoins(row.totalCoins)}
                    </span>
                    <span className="usage-panel__row-count">
                      {row.count.toLocaleString('en-US')}
                    </span>
                    <span className="usage-panel__row-percent">
                      {totalCharged > 0 ? `${pct.toFixed(0)}%` : '—'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {zeroRows.length > 0 && !isEmpty && !allZeros && (
            <button
              type="button"
              className="usage-panel__zero-toggle"
              onClick={() => setShowZeroRows((s) => !s)}
              aria-expanded={showZeroRows}
            >
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`usage-panel__zero-chevron${
                  showZeroRows ? ' usage-panel__zero-chevron--open' : ''
                }`}
              />
              {showZeroRows
                ? `Hide ${zeroRows.length} resource${
                    zeroRows.length === 1 ? '' : 's'
                  } with no charges`
                : `${zeroRows.length} resource${
                    zeroRows.length === 1 ? '' : 's'
                  } with no charges this period`}
            </button>
          )}

          {(periodStart || periodEnd) && (
            <p className="usage-panel__range">
              {formatDate(periodStart)} — {formatDate(periodEnd)}
            </p>
          )}
        </>
      )}
    </section>
  );
}
